import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import "trix";
import "trix/dist/trix.css";
import Swal from "sweetalert2";
import { AuthContext } from "../contexts/auth";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { companyVehiclesList, companyVehicleCreate } from "../services/api_vehicle_employee";
import { patchRichiestaRimborso } from "../services/api_cost_refunds";

const FUEL_CHOICES = [
  ["benzina", "Benzina"],
  ["diesel", "Diesel"],
  ["gpl", "GPL"],
  ["metano", "Metano"],
  ["ibrida", "Ibrida"],
  ["elettrica", "Elettrica"],
  ["altro", "Altro"],
];

const FormEditRimborsoKmCompany = ({ afterSubmitEditRecord, recordToEdit, onCancelEdit }) => {
  const { token } = useContext(AuthContext);

  
  // ---- stato file allegato ----
  const [file, setFile] = useState(recordToEdit.file || null);

  // ---- stato form richiesta ----
  const [formValues, setFormValues] = useState({
    title: recordToEdit.title || "",
    date: recordToEdit.date || "",
    // override tariffa PER QUESTA RICHIESTA (opzionale)
    requestRatePerKm:
      recordToEdit.rate_per_km !== null && recordToEdit.rate_per_km !== undefined
        ? String(recordToEdit.rate_per_km)
        : "",
    departureAddress: recordToEdit.departure_location || "",
    arrivalAddress: recordToEdit.arrival_location || "",
    distanceKm: recordToEdit.distance_km || "",
    description: recordToEdit.description || "",
    // campi NUOVO veicolo (se non ne seleziono uno esistente)
    licensePlate: recordToEdit.vehicle?.license_plate || "",
    vehicleBrand: recordToEdit.vehicle?.vehicle_brand || "",
    vehicleType: recordToEdit.vehicle?.vehicle_type || "",
    vehicleMotor: recordToEdit.vehicle?.vehicle_motor || "",
    vehicleRatePerKm:
      recordToEdit.vehicle?.rate_per_km != null ? String(recordToEdit.vehicle.rate_per_km) : "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const descriptionRef = useRef(null);

  // ---- veicoli del dipendente della richiesta ----
  const employeeId = recordToEdit?.employee?.id;
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(
    recordToEdit?.vehicle?.id ? String(recordToEdit.vehicle.id) : ""
  );

  const normalizePlate = (v) => String(v || "").toUpperCase().replace(/\s+/g, "");

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    const next = name === "licensePlate" ? normalizePlate(value) : value;
    setFormValues((prev) => ({ ...prev, [name]: next }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Trix init + sync
  useEffect(() => {
    const editor = document.querySelector("trix-editor");
    if (recordToEdit?.description) {
      descriptionRef.current.value = recordToEdit.description;
      editor.editor.loadHTML(recordToEdit.description);
    }
    const handler = (ev) => {
      const html = ev.target.innerHTML;
      setFormValues((p) => ({ ...p, description: html }));
      if (descriptionRef.current) descriptionRef.current.value = html;
    };
    editor.addEventListener("trix-change", handler);
    return () => editor.removeEventListener("trix-change", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordToEdit?.id]);

  // Carica veicoli del dipendente
  useEffect(() => {
    (async () => {
      if (!employeeId) {
        setVehicles([]);
        return;
      }
      try {
        const res = await companyVehiclesList(token, { employee: employeeId, pageSize: 100 });
        setVehicles(Array.isArray(res?.results) ? res.results : []);
      } catch (e) {
        console.error(e);
        setVehicles([]);
      }
    })();
  }, [token, employeeId]);

  // Opzioni listbox veicoli
  const vehiclePlaceholder = { id: "", name: "Seleziona un veicolo" };
  const vehicleOptions = useMemo(
    () => [
      vehiclePlaceholder,
      ...(vehicles || []).map((v) => ({
        id: String(v.id),
        name: `${v.license_plate} — ${v.vehicle_brand}${v.vehicle_type ? ` ${v.vehicle_type}` : ""}${
          v.rate_per_km != null
            ? ` • ${Number(v.rate_per_km).toLocaleString("it-IT", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} €/km`
            : ""
        }`,
        v,
      })),
    ],
    [vehicles]
  );
  const selectedVehicleOption =
    vehicleOptions.find((o) => String(o.id) === String(selectedVehicleId)) || vehiclePlaceholder;

  const onPickVehicle = (opt) => {
    const pickedId = opt?.id || "";
    setSelectedVehicleId(pickedId);

    if (opt?.v) {
      const v = opt.v;
      // popolo i campi (solo per visibilità; rimangono disabilitati con veicolo selezionato)
      setFormValues((prev) => ({
        ...prev,
        licensePlate: v.license_plate || "",
        vehicleBrand: v.vehicle_brand || "",
        vehicleType: v.vehicle_type || "",
        vehicleMotor: v.vehicle_motor || "",
        vehicleRatePerKm: v.rate_per_km != null ? String(v.rate_per_km) : "",
      }));
    } else {
      // nessun veicolo selezionato → possibile creazione nuovo
      setFormValues((prev) => ({
        ...prev,
        licensePlate: "",
        vehicleBrand: "",
        vehicleType: "",
        vehicleMotor: "",
        vehicleRatePerKm: "",
      }));
    }
  };

  // Se digito una targa esistente → auto-select
  useEffect(() => {
    const lp = normalizePlate(formValues.licensePlate);
    if (!lp || vehicles.length === 0) return;
    const v = vehicles.find((x) => normalizePlate(x.license_plate) === lp);
    if (v) {
      setSelectedVehicleId(String(v.id));
      setFormValues((prev) => ({
        ...prev,
        vehicleBrand: v.vehicle_brand || "",
        vehicleType: v.vehicle_type || "",
        vehicleMotor: v.vehicle_motor || "",
        vehicleRatePerKm: v.rate_per_km != null ? String(v.rate_per_km) : "",
      }));
    }
  }, [formValues.licensePlate, vehicles]);

  // Upload allegato richiesta
  const onDrop = useCallback((accepted) => {
    const selectedFile = accepted[0];
    if (selectedFile?.size > 2 * 1024 * 1024) {
      Swal.fire("Errore", "File troppo grande. Massimo 2MB", "warning");
      return;
    }
    if (selectedFile) setFile(selectedFile);
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ onDrop, multiple: false });

  // Validazione
  const validateForm = () => {
    const errors = {};
    if (!formValues.title) errors.title = "Oggetto richiesto";
    if (!formValues.date) errors.date = "Data richiesta";
    if (!formValues.departureAddress) errors.departureAddress = "Indirizzo partenza richiesto";
    if (!formValues.arrivalAddress) errors.arrivalAddress = "Indirizzo arrivo richiesto";
    if (!formValues.distanceKm || isNaN(formValues.distanceKm) || parseFloat(formValues.distanceKm) <= 0) {
      errors.distanceKm = "Numero km valido richiesto";
    }
    // Se NON seleziono un veicolo → creo un veicolo nuovo: richiedo campi minimi
    if (!selectedVehicleId) {
      if (!formValues.licensePlate) errors.licensePlate = "Targa richiesta";
      if (!formValues.vehicleBrand) errors.vehicleBrand = "Marca richiesta";
      if (!formValues.vehicleType) errors.vehicleType = "Modello richiesto";
      if (!formValues.vehicleMotor) errors.vehicleMotor = "Alimentazione richiesta";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      let vehicleId = selectedVehicleId;

      // se non ho selezionato un veicolo esistente, ne creo uno nuovo
      if (!vehicleId) {
        const created = await companyVehicleCreate(token, {
          employee: Number(employeeId),
          license_plate: normalizePlate(formValues.licensePlate),
          vehicle_brand: (formValues.vehicleBrand || "").trim(),
          vehicle_type: (formValues.vehicleType || "").trim(),
          vehicle_motor: formValues.vehicleMotor || "",
          // tariffa anagrafica opzionale
          rate_per_km: formValues.vehicleRatePerKm !== "" ? formValues.vehicleRatePerKm : null,
        });
        vehicleId = String(created.id);
        setVehicles((prev) => [created, ...prev]);
        setSelectedVehicleId(vehicleId);
      }

      // prepara FormData per PATCH mileage (nuova struttura)
      const fd = new FormData();
      fd.append("title", formValues.title);
      fd.append("date", formValues.date);
      fd.append("departure_location", formValues.departureAddress);
      fd.append("arrival_location", formValues.arrivalAddress);
      fd.append("distance_km", String(formValues.distanceKm));
      fd.append("description", formValues.description || "");
      fd.append("type", "mileage");
      fd.append("vehicle", String(vehicleId)); // <-- SOLO PK del veicolo

      // override tariffa per questa richiesta:
      // - ""  => invio stringa vuota per forzare NULL (coerced nel serializer)
      // - "0.35" => override esplicito
      fd.append(
        "rate_per_km",
        formValues.requestRatePerKm === "" ? "" : String(formValues.requestRatePerKm)
      );

      // allegato:
      // - se file è un File => upload
      // - se l'utente ha rimosso un file precedentemente presente => invio stringa vuota per cancellare
      const hadFileBefore = Boolean(recordToEdit.file);
      const hasNewFile = file && file !== recordToEdit.file;
      if (hasNewFile) {
        fd.append("file_binary", file);
      } else if (hadFileBefore && !file) {
        // rimozione allegato esistente
        fd.append("file_binary", "");
      }
      // se file invariato, non invio nulla

      const updated = await patchRichiestaRimborso(token, recordToEdit.id, fd, "mileage");
      await afterSubmitEditRecord(updated);

      Swal.fire({ icon: "success", title: "Aggiornato!", timer: 1500, showConfirmButton: false });
    } catch (err) {
      let msg = "Errore durante l'aggiornamento.";
      if (err?.response?.data) {
        const details = [];
        Object.entries(err.response.data).forEach(([k, v]) => {
          if (Array.isArray(v)) details.push(`${k}: ${v.join(", ")}`);
          else if (typeof v === "string") details.push(`${k}: ${v}`);
        });
        if (details.length) msg = details.join("\n");
      }
      Swal.fire({ icon: "error", title: "Errore", text: msg });
    } finally {
      setLoading(false);
    }
  };

  const css = `
    .trix-button.trix-button--icon.trix-button--icon-attach { display: none; }
    .trix-button-group.trix-button-group--file-tools { display: none; }
  `;

  const vehicleInputsDisabled = Boolean(selectedVehicleId);

  return (
    <div className="shadow-md border border-gray-200 rounded-b-lg px-8 pt-6 pb-8 bg-white">
      <style>{css}</style>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Oggetto e Data */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Oggetto *</label>
            <input
              type="text"
              name="title"
              value={formValues.title}
              onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm"
            />
            {formErrors.title && <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Data *</label>
            <input
              type="date"
              name="date"
              value={formValues.date}
              onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm"
            />
            {formErrors.date && <p className="text-red-500 text-xs mt-1">{formErrors.date}</p>}
          </div>
        </div>

        {/* Veicolo associato */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Veicolo associato</label>
          <Listbox value={selectedVehicleOption} onChange={onPickVehicle}>
            <div className="relative mt-1">
              <Listbox.Button className="relative h-10 w-full cursor-default rounded-md bg-white py-2 pl-3 pr-10 text-left shadow-sm border border-gray-300 focus:outline-none sm:text-sm">
                <span className="block truncate">{selectedVehicleOption?.name || vehiclePlaceholder.name}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </Listbox.Button>
              <Transition leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                <Listbox.Options className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                  {vehicleOptions.map((opt) => (
                    <Listbox.Option
                      key={opt.id || "placeholder"}
                      value={opt}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                          active ? "bg-indigo-100 text-gray-900" : "text-gray-900"
                        }`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>{opt.name}</span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-800">
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
          <p className="text-xs text-gray-500 mt-1">
            Lascia “Seleziona un veicolo” per inserirne uno nuovo (campi sotto).
          </p>
        </div>

        {/* Campi NUOVO veicolo (disabilitati se ne selezioni uno esistente) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Targa {selectedVehicleId ? "" : "*"}</label>
            <input
              type="text"
              name="licensePlate"
              value={formValues.licensePlate}
              onChange={handleFieldChange}
              disabled={vehicleInputsDisabled}
              className={`mt-1 p-2 block w-full border rounded-md shadow-sm uppercase placeholder:text-gray-300 ${
                vehicleInputsDisabled ? "bg-gray-100 cursor-not-allowed border-gray-200" : "border-gray-300"
              }`}
              placeholder="AA123AA"
            />
            {formErrors.licensePlate && <p className="text-red-500 text-xs mt-1">{formErrors.licensePlate}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Marca {selectedVehicleId ? "" : "*"}</label>
            <input
              type="text"
              name="vehicleBrand"
              value={formValues.vehicleBrand}
              onChange={handleFieldChange}
              disabled={vehicleInputsDisabled}
              className={`mt-1 p-2 block w-full border rounded-md shadow-sm ${
                vehicleInputsDisabled ? "bg-gray-100 cursor-not-allowed border-gray-200" : "border-gray-300"
              }`}
            />
            {formErrors.vehicleBrand && <p className="text-red-500 text-xs mt-1">{formErrors.vehicleBrand}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Modello {selectedVehicleId ? "" : "*"}</label>
            <input
              type="text"
              name="vehicleType"
              value={formValues.vehicleType}
              onChange={handleFieldChange}
              disabled={vehicleInputsDisabled}
              className={`mt-1 p-2 block w-full border rounded-md shadow-sm ${
                vehicleInputsDisabled ? "bg-gray-100 cursor-not-allowed border-gray-200" : "border-gray-300"
              }`}
            />
            {formErrors.vehicleType && <p className="text-red-500 text-xs mt-1">{formErrors.vehicleType}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Alimentazione {selectedVehicleId ? "" : "*"}
            </label>

            <Listbox
              value={formValues.vehicleMotor || ""}
              onChange={(val) => handleFieldChange({ target: { name: "vehicleMotor", value: val } })}
              disabled={vehicleInputsDisabled}
            >
              {({ open, disabled }) => (
                <div className="relative mt-1">
                  {/* Button */}
                  <Listbox.Button
                    className={[
                      "relative w-full rounded-md border py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none",
                      disabled
                        ? (selectedVehicleId
                            ? "bg-gray-100 cursor-not-allowed border-gray-200 text-gray-600"
                            : "bg-white cursor-not-allowed border-gray-300 text-gray-400")
                        : "bg-white border-gray-300"
                    ].join(" ")}
                  >
                    <span className="block truncate">
                      {formValues.vehicleMotor
                        ? (FUEL_CHOICES.find(([v]) => v === formValues.vehicleMotor)?.[1] || formValues.vehicleMotor)
                        : "—"}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </Listbox.Button>

                  {/* Options */}
                  <Transition
                    show={open}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options
                      className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base
                                shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                    >
                      {/* Opzione vuota */}
                      <Listbox.Option
                        key="__empty__"
                        value=""
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-3 pr-9 ${
                            active ? "bg-indigo-50 text-indigo-900" : "text-gray-900"
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>—</span>
                            {selected ? (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-600">✓</span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>

                      {FUEL_CHOICES.map(([val, label]) => (
                        <Listbox.Option
                          key={val}
                          value={val}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-3 pr-9 ${
                              active ? "bg-indigo-50 text-indigo-900" : "text-gray-900"
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>{label}</span>
                              {selected ? (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-600">✓</span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              )}
            </Listbox>

            {formErrors.vehicleMotor && (
              <p className="text-red-500 text-xs mt-1">{formErrors.vehicleMotor}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Tariffa €/km (anagrafica veicolo)
              <span className="block text-xs text-gray-500">Opzionale — solo se crei un nuovo veicolo.</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="vehicleRatePerKm"
              value={formValues.vehicleRatePerKm}
              onChange={handleFieldChange}
              disabled={vehicleInputsDisabled}
              className={`mt-1 p-2 block w-full border rounded-md shadow-sm placeholder:text-gray-300 ${
                vehicleInputsDisabled ? "bg-gray-100 cursor-not-allowed border-gray-200" : "border-gray-300"
              }`}
              placeholder="es. 0.35"
            />
          </div>
                    <div>
            <label className="block text-sm font-medium text-gray-700">
              Tariffa €/km (per questa richiesta)
              <span className="block text-xs text-gray-500">Lascia vuoto per usare la tariffa del veicolo</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="requestRatePerKm"
              value={formValues.requestRatePerKm}
              onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm placeholder:text-gray-300"
              placeholder="es. 0.35"
            />
          </div>
        </div>

        {/* partenza e arrivo */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
           <div>
            <label className="block text-sm font-medium text-gray-700">Indirizzo Partenza *</label>
            <input
              type="text"
              name="departureAddress"
              value={formValues.departureAddress}
              onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm"
            />
            {formErrors.departureAddress && (
              <p className="text-red-500 text-xs mt-1">{formErrors.departureAddress}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Indirizzo Arrivo *</label>
            <input
              type="text"
              name="arrivalAddress"
              value={formValues.arrivalAddress}
              onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm"
            />
            {formErrors.arrivalAddress && <p className="text-red-500 text-xs mt-1">{formErrors.arrivalAddress}</p>}
          </div>
        </div>
        
        {/* distanza km percorsi */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Distanza Km *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="distanceKm"
              value={formValues.distanceKm}
              onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm"
            />
            {formErrors.distanceKm && <p className="text-red-500 text-xs mt-1">{formErrors.distanceKm}</p>}
          </div>
        </div>

        {/* Allegato */}
        <div className="mt-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Allegato</label>

          {recordToEdit?.file && file && recordToEdit.file === file && (
            <div className="mb-2">
              <span className="text-sm text-gray-700 mr-2">File caricato:</span>
              <a
                href={recordToEdit.file}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold bg-indigo-500 hover:bg-indigo-400 p-1 text-white rounded-md"
              >
                Visualizza
              </a>
              <button
                type="button"
                onClick={() => setFile(null)}
                className="ml-3 text-sm text-red-600 font-semibold underline"
              >
                Elimina
              </button>
            </div>
          )}

          {recordToEdit?.file !== file && file && typeof file !== "string" && (
            <div className="mb-2">
              <p className="text-sm text-gray-700">Nuovo file selezionato:</p>
              <ul className="text-sm text-gray-600 list-disc pl-5">
                <li>
                  <strong>Nome:</strong> {file.name}
                </li>
                <li>
                  <strong>Tipo:</strong> {file.type}
                </li>
                <li>
                  <strong>Dimensione:</strong> {(file.size / 1024).toFixed(2)} KB
                </li>
              </ul>
              <button
                type="button"
                onClick={() => setFile(null)}
                className="mt-1 text-red-500 text-sm font-semibold underline"
              >
                Rimuovi
              </button>
            </div>
          )}

          {!file && (
            <div
              {...getRootProps({
                className:
                  "border-dashed border-2 border-gray-300 p-4 rounded-md cursor-pointer text-center text-sm text-gray-500",
              })}
            >
              <input {...getInputProps()} />
              <p>Trascina qui il file o clicca per selezionarlo (max 2MB)</p>
            </div>
          )}
        </div>

        {/* Note */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Note aggiuntive</label>
          <input
            type="hidden"
            id="description"
            name="description"
            ref={descriptionRef}
            value={formValues.description}
            readOnly
          />
          <trix-editor input="description"></trix-editor>
        </div>

        {/* Submit */}
        <div className="mt-6 flex justify-center gap-4">
          {loading ? (
            <div className="text-sm font-semibold text-gray-900">Salvataggio in corso...</div>
          ) : (
            <>
              <button
                type="submit"
                className="bg-indigo-500 text-white text-sm px-4 py-2 rounded-md hover:bg-indigo-400 w-24 font-semibold"
              >
                Aggiorna
              </button>
              <button
                type="button"
                onClick={onCancelEdit}
                className="bg-gray-200 text-gray-600 text-sm px-4 py-2 rounded-md hover:bg-gray-300 w-24 font-semibold"
              >
                Annulla
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default FormEditRimborsoKmCompany;